import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box, Card, Divider, IconButton, Menu, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState, useCallback, useEffect } from "react";
import { numberWithCommas, sentenceCase } from "../../../utils/customFunctions";
import ButtonComponent from '../../../components/form/buttonComponent';
import { useNavigate } from "react-router-dom";
import { GetCalculatorInfo } from "../../../context/calculator/tour/calculatorProvider";
import DialogComponent from "../../../components/dialog/dialogComponent";
import SnackbarComponent from "../../../components/snackBar/snackbarComponent";
import { GetPastTrips } from "../../../context/trip/tripProvider";
import { CALCULATOR_TAGS } from "../../../data/data";
import { FormProviderComponent, TextFieldComponent } from '../../../components/form';
import InfoIcon from '@mui/icons-material/Info';
import { useDeleteATourMutation, useEditATourMutation, useSaveATourMutation } from '../../../services/tripApi';
import { GetAuth } from '../../../context/auth/authProvider';
import CloseIcon from '@mui/icons-material/Close';
import { getAdditionalExistingTourDayBasedDistance, getAdditionalNewTourDayBasedDistance, getExistingTotalRateUSD, getExistingTourActualRateWithoutBatta, getExistingTourAdditionalKm, getExistingTourBatta, getExistingTourMinimumKm, getExistingTourRoundedKm, getExistingTourRoundedKmOffset, getExistingTourTotalBatta, getExistingTourTotalRateLKR, getExistingTourUSDConversionRate, getMarginLKR, getMarginUSDRate, getNewTotalRateUSD, getNewTourActualRateWithoutBatta, getNewTourAdditionalKm, getNewTourBatta, getNewTourMinimumKm, getNewTourRoundedKm, getNewTourRoundedKmOffset, getNewTourTotalBatta, getNewTourTotalRateLKR, getNewTourUSDConversionRate, getSeasonRate, getSeasonType } from './tourCalculatorUtils';
import AddCircleIcon from '@mui/icons-material/AddCircle';

const resultsRow = (label, value, highlight=false, openBrief=null) => {
    return <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        width='100%'
    >
        <Typography
            fontWeight={400}
            fontSize={{xs: highlight ? 16 : 14, md: highlight ? 20 : 18}}
            letterSpacing={0.3}
            color='black'
            textAlign='left'
        >
            {label}
        </Typography>

        <Stack
            direction='row'
            alignItems='center'
            justifyContent='end'
        >
            <Typography
                fontWeight={highlight ? 700 : 400}
                fontSize={{xs: highlight ? 16 : 14, md: highlight ? 20 : 18}}
                letterSpacing={0.36}
                color={highlight ? 'black' : '#5E5959'}
                textAlign='right'
            >
                {value}
            </Typography>

            {
                openBrief
                ?
                <IconButton 
                    aria-label="info" 
                    onClick={openBrief}
                    sx={{
                        p: 0,
                        ml: 0.5
                    }}
                >
                    <InfoIcon 
                        sx={{ 
                            color: '#ea6142',
                            fontSize: 20
                        }}
                    />
                </IconButton>
                :
                <></>
            }
        </Stack>
    </Stack>
}

const CalculatorResults = ({
    backToFilterView,
    summaryData,
    loadAllTours,
    modelViewClose=null,
    sx
}) => {
    const theme = useTheme();

    const navigate = useNavigate();

    const [data, setData] = useState(null);

    const info = GetCalculatorInfo();
    const auth = GetAuth();

    const formSchema = yup.object().shape({
        tripName: yup.string().required('Trip name is required')
    });
            
    const defaultValues = {
        tripName: ""
    };
            
    const formMethods = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: defaultValues
    });
            
    const {
        setValue,
        reset: reset,
        handleSubmit: handleSubmit,
        formState: { isSubmitting: isSubmitting },
        watch
    } = formMethods;
            
    const values = watch();

    useEffect(() => {
        if(summaryData){
            console.log("Received summary...");
            console.log(summaryData);

            setData(summaryData);
        }
        else if(info){
            console.log("Received shared data...");
            console.log(info.calculatorInfo);

            if(info.calculatorInfo.id){
                setValue("tripName", info.calculatorInfo.tripName);
            }

            setData(info.calculatorInfo);
        }
    }, [summaryData, info]);

    useEffect(() => {
        if(data){
            console.log("Data...");
            console.log(data);
        }
    }, [data]);

    const trip = GetPastTrips();

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);

    const [snackBar, setSnackBar] = useState({
        message: "",
        open: false,
        color: ""
    });

    // COORDINATES
    const getCoordinatesInfo = useCallback(() => {
        if(data?.locations){
            return data.locations.map(loc => {
                if(loc?.location){
                    return JSON.parse(loc.location);
                }
                return { lat: loc.lat, lng: loc.lng };
            });
        }
        else{
            return [];
        }
    }, [data]);
    const hasCoordinates = useCallback(() => {
        return getCoordinatesInfo().length > 0;
    }, [getCoordinatesInfo]);

    const requestSave = useCallback(() => {
        setShowSaveDialog(true);
    }, []);

    const [
        saveATour,
        {
            isLoading: isLoadingTourSave,
            isSuccess: isSuccessTourSave,
            isError: isErrorTourSave,
            error: errorTourSave 
        }
    ] = useSaveATourMutation();

    const [
        editATour,
        {
            isLoading: isLoadingTourEdit,
            isSuccess: isSuccessTourEdit,
            isError: isErrorTourEdit,
            error: errorTourEdit 
        }
    ] = useEditATourMutation();

    const confirmSave = useCallback(() => {
        console.log(data);

        const body = {
            "batta": true,
            "days": data.days,
            "distance": data.distance,
            "month": data.month,
            "perKm": data.perKm,
            "staffId": auth?.currentUser?.staffID,
            "time": {
                "hour": data.time?.hour ?? data.hour ?? 0,
                "minute": data.time?.minute ?? data.minute ?? 0
            },
            "vehicle": data.vehicle,
            "battaAmount": getBatta(),
            "kmOffsetFor7Day": getAdditionalDayBasedDistance(),
            "minKmPerDay": getMinimumKm(),
            "tripName": values.tripName,
            "usdConversion": getUSDConversionRate(),
            "locations": getCoordinatesInfo()
        }

        console.log(body);

        setShowSaveDialog(false);

        if(data.id){
            editATour({id: data.id, request: body});
        }
        else{
            saveATour({body: body});
        }
    }, [trip, data, info, values, auth, getCoordinatesInfo]);

    useEffect(() => {
        if(isSuccessTourSave){
            setSnackBar({
                message: "Successfully saved!",
                open: true,
                color: "green"
            });

            info?.clearCalculator();
            trip.clearActiveTour();
            trip.clearEditTourHistory();

            setTimeout(() => {
                navigate('/records?type=tours');
            }, 1000);
        }
        else if(isErrorTourSave){
            setSnackBar({
                message: errorTourSave?.data?.message ?? "Something went wrong",
                open: true,
                color: "red"
            });
        }
    }, [isErrorTourSave, errorTourSave, isSuccessTourSave]);

    useEffect(() => {
        if(isSuccessTourEdit){
            setSnackBar({
                message: "Successfully updated!",
                open: true,
                color: "green"
            });

            info?.clearCalculator();
            trip.clearActiveTour();
            trip.clearEditTourHistory();

            setTimeout(() => {
                navigate('/records');
            }, 1000);
        }
        else if(isErrorTourEdit){
            setSnackBar({
                message: errorTourEdit?.data?.message ?? "Something went wrong",
                open: true,
                color: "red"
            });
        }
    }, [isErrorTourEdit, errorTourEdit, isSuccessTourEdit]);

    const deleteTrip = useCallback(() => {
        setShowDeleteDialog(true);
    }, []);

    const [
        deleteATour,
        {
            isLoading: isLoadingTourDeletion,
            isSuccess: isSuccessTourDeletion,
            isError: isErrorTourDeletion,
            error: errorTourDeletion 
        }
    ] = useDeleteATourMutation();

    useEffect(() => {
        if(isSuccessTourDeletion){
            setSnackBar({
                message: "Successfully deleted!",
                open: true,
                color: "green"
            });

            setTimeout(() => {
                if(modelViewClose) modelViewClose();
                
                loadAllTours();
            }, 1000);
        }
        else if(isErrorTourDeletion){
            console.log("Tour Delete Error...");
            console.log(errorTourDeletion);

            if(errorTourDeletion.originalStatus === 200){
                setSnackBar({
                    message: "Successfully deleted!",
                    open: true,
                    color: "green"
                });

                setTimeout(() => {
                    if(modelViewClose) modelViewClose();

                    loadAllTours();
                }, 1000);
            }
            else{
                setSnackBar({
                    message: errorTourDeletion?.data?.message ?? "Something went wrong",
                    open: true,
                    color: "red"
                });
            }
        }
    }, [isErrorTourDeletion, errorTourDeletion, isSuccessTourDeletion]);

    const confirmDeleteTrip = useCallback(() => {
        setShowDeleteDialog(false);
        
        deleteATour({id: summaryData.id, staffId: auth?.currentUser?.staffID});
    }, [data, summaryData]);

    const editATrip = useCallback(() => {
        console.log("BBBBBBB");
        console.log(summaryData);
        trip.editTour(summaryData);

        navigate('/calculator?type=tours');
    }, [summaryData]);

    const backToTripCalculations = useCallback(() => {
        console.log(data);

        trip.editTour(data);

        backToFilterView();                            
    }, [data]);

    const [anchorElDistanceBrief, setAnchorElDistanceBrief] = useState(null);
    const [openDistanceBrief, setOpenDistanceBrief] = useState(null);

    const handleClickDistanceBrief = useCallback((event) => {
        setAnchorElDistanceBrief(event.currentTarget);
        setOpenDistanceBrief(true);
    }, []);
    const handleCloseDistanceBrief = useCallback((event) => {
        setAnchorElDistanceBrief(null);
        setOpenDistanceBrief(false);
    }, []);

    const [anchorElPaymentBrief, setAnchorElPaymentBrief] = useState(null);
    const [openPaymentBrief, setOpenPaymentBrief] = useState(null);

    const handleClickPaymentBrief = useCallback((event) => {
        setAnchorElPaymentBrief(event.currentTarget);
        setOpenPaymentBrief(true);
    }, []);
    const handleClosePaymentBrief = useCallback((event) => {
        setAnchorElPaymentBrief(null);
        setOpenPaymentBrief(false);
    }, []);

    // CALCULATIONS
    // Overview
    const getTime = useCallback(() => {
        const t = data?.time ?? data;

        let elapsedTime = "N/A";

        if(t){
            const hr = Number.parseInt(t.hour ?? 0);
            const min = Number.parseInt(t.minute ?? 0);

            if(hr === 0 && min === 0){
                return elapsedTime;
            }

            if(hr > 0){
                elapsedTime = `${hr} Hr `;
            }
            else{
                elapsedTime = "";
            }

            if(min > 0){
                elapsedTime += `${min} Min`;
            }
        }

        return elapsedTime;
    }, [data]);

    const getRoundedKm = useCallback(() => {
        if(data){
            if(summaryData){
                return getExistingTourRoundedKm(data, hasCoordinates());
            }
            else{
                return getNewTourRoundedKm(data, info, CALCULATOR_TAGS, hasCoordinates());
            }
        }
        
        return 0;
    }, [summaryData, data]);
    
    const getTotalRateLKR = useCallback(() => {
        if(data){
            let rate = 0;

            if(summaryData){
                rate = getExistingTourTotalRateLKR(data, hasCoordinates());
            }
            else{
                rate = getNewTourTotalRateLKR(data, info, CALCULATOR_TAGS, hasCoordinates());
            }

            return numberWithCommas(rate.toFixed(2));
        }
    
        return 0;
    }, [summaryData, data]);

    const getTotalRateUSD = useCallback(() => {
        if(data){
            let rate = 0;

            if(summaryData){
                rate = getExistingTotalRateUSD(data, hasCoordinates());
            }
            else{
                rate = getNewTotalRateUSD(data, info, CALCULATOR_TAGS, hasCoordinates());
            }

            return numberWithCommas(rate);
        }
    
        return 0;
    }, [summaryData, data]);

    // Distance Breakdown
    const getMinimumKm = useCallback(() => {
        if(summaryData){
            return getExistingTourMinimumKm(data);
        }
        else{
            return getNewTourMinimumKm(info, CALCULATOR_TAGS);
        }
    }, [summaryData, data]);

    const getAdditionalDayBasedDistance = useCallback(() => {        
        if(data){
            if(summaryData){
                return getAdditionalExistingTourDayBasedDistance(data, hasCoordinates());
            }
            else{
                return getAdditionalNewTourDayBasedDistance(data, info, CALCULATOR_TAGS, hasCoordinates());
            }
        }

        return 0;
    }, [summaryData, data]);

    const getAdditionalKm = useCallback(() => {
        if(data){
            if(summaryData){
                return getExistingTourAdditionalKm(data, hasCoordinates());
            }
            else{
                return getNewTourAdditionalKm(data, info, CALCULATOR_TAGS, hasCoordinates());
            }
        }

        return 0;
    }, [summaryData, data]);

    const getRoundedKmOffset = useCallback(() => {
        if(data){
            if(summaryData){
                return getExistingTourRoundedKmOffset(data, hasCoordinates());
            }
            else{
                return getNewTourRoundedKmOffset(data, info, CALCULATOR_TAGS, hasCoordinates());
            }
        }

        return 0;
    }, [summaryData, data]);

    // Rate Overview
    const getBatta = useCallback(() => {
        if(summaryData){
            return getExistingTourBatta(data);
        }
        else{
            return getNewTourBatta(info, CALCULATOR_TAGS.BATTA);
        }
    }, [summaryData, data]);

    const getUSDConversionRate = useCallback(() => {
        let usdConversion = 0;

        if(summaryData){
            usdConversion = getExistingTourUSDConversionRate(data);
        }
        else{
            usdConversion = getNewTourUSDConversionRate(info, CALCULATOR_TAGS);
        }

        return usdConversion;
    }, [summaryData, data]);

    const getActualRateWithoutBatta = useCallback(() => {
        if(data){
            if(summaryData){
                return getExistingTourActualRateWithoutBatta(data, hasCoordinates());
            }
            else{
                return getNewTourActualRateWithoutBatta(data, info, CALCULATOR_TAGS, hasCoordinates());
            }
        }

        return 0;
    }, [summaryData, data]);

    const getBattaRate = useCallback(() => {
        if(data){
            let rate = 0;

            if(summaryData){
                rate = getExistingTourTotalBatta(data);
            }
            else{
                rate = getNewTourTotalBatta(data, info, CALCULATOR_TAGS.BATTA);
            }

            return numberWithCommas(rate.toFixed(2));
        }

        return 0;
    }, [summaryData, data]);

    const getSeasonalRate = useCallback(() => {
        if(data){
            return getSeasonRate(data, getUSDConversionRate());
        }

        return 0;
    }, [data, getUSDConversionRate]);

    const getMarginLKRRate = useCallback(() => {
        if(data){
            return getMarginLKR(data, getUSDConversionRate());
        }

        return 0;
    }, [data, getUSDConversionRate]);

    return (
        <>
            <Box 
                px={{xs: summaryData ? 0 : 2, md: summaryData ? 0 : 7}}
                pb={{xs: summaryData ? 0 : 15, md: 0}}
                sx={{
                    maxHeight: '100vh',
                    overflowY: 'scroll',
                    ...sx
                }}
            >
                <Stack
                    direction='column'
                    justifyContent='start'
                    alignItems='center'
                    width='100%'
                    py={{xs: summaryData ? 0 : 2, md: summaryData ? 0 : 9}}
                    data-aos="zoom-in"
                >
                    {
                        !summaryData
                        ?
                        <>
                            {/* Title */}
                            <Typography
                                fontWeight={800}
                                fontSize={40}
                                letterSpacing={0.36}
                                color={theme.palette.primary.main}
                                textAlign='center'
                                sx={{
                                    display: {xs: 'none', md: 'block'}
                                }}
                            >
                                Trip Rate Calculation
                            </Typography>

                            {/* Back */}
                            <Stack
                                direction='row'
                                justifyContent='end'
                                alignItems='center'
                                width='100%'
                                spacing={1}
                                mt={{xs: 2, md: 4}}
                            >
                                <ButtonComponent 
                                    label='New Tour'
                                    onClick={() => {
                                        navigate('/map');
                                    }}
                                    sx={{
                                        width: 'fit-content',
                                        fontWeight: 500,
                                        fontSize: {xs: 12, md: 16},
                                        letterSpacing: 0.36,
                                        p: {xs: 1, md: 1.5}
                                    }}
                                    variant="outlined"
                                    startIcon={<AddCircleIcon />}
                                />
                            </Stack>
                        </>
                        :
                        <></>
                    }

                    {
                        modelViewClose
                        ?
                        <Stack
                            direction='row'
                            alignItems='center'
                            position='absolute'
                            width='100%'
                        >
                            <Stack
                                direction='row'
                                justifyContent='center'
                                alignItems='center'
                                width='100%'
                                spacing={1}
                                position='absolute'
                            >
                                <Typography
                                    fontWeight={600}
                                    color={theme.palette.primary.main}
                                    fontSize={{xs: 16, lg: 20}}
                                >
                                    {data?.tripName}
                                </Typography>
                            </Stack>

                            <Stack
                                direction='row'
                                justifyContent='end'
                                alignItems='center'
                                width='100%'
                                spacing={1}
                            >
                                <IconButton onClick={modelViewClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                        :
                        <></>
                    }

                    <Card 
                        elevation={summaryData ? 0 : 3}
                        sx={{
                            width: '100%',
                            mt: {xs: summaryData ? 0 : 2, md: summaryData ? 0 : 3},
                            pt: {xs: modelViewClose ? 4 : 0, md: modelViewClose ? 8 : 0}
                        }}
                    >
                        <Box sx={{p: {xs: 3, md: summaryData ? 5 : 7}}}>
                            <Stack
                                direction='column'
                                justifyContent='start'
                                alignItems='center'
                                width='100%'
                                spacing={{xs: 2.5, md: 3.5}}
                            >
                                {resultsRow('Month', data?.month)}
                                {resultsRow('Vehicle', data?.vehicle ? sentenceCase(data.vehicle) : 'N/A')}
                                {resultsRow('Total Distance', `${getRoundedKm()} km`, false, handleClickDistanceBrief)}
                                {resultsRow('Duration', getTime())}
                                {resultsRow('Days', data?.days ?? 'N/A')}
                                {resultsRow('Rate Per Km', `LKR ${numberWithCommas(data?.perKm)}`)}
                                {resultsRow('Batta Per Day', `LKR ${data?.batta ? getBatta() : 0}`)}

                                {/* Divider */}
                                <Box 
                                    sx={{
                                        width: '100%',
                                        py: 1
                                    }}
                                >
                                    <Divider 
                                        sx={{
                                            width: '100%',
                                            border: '0.5px solid #D9D9D9'
                                        }}
                                    />
                                </Box>

                                {resultsRow('Total (LKR)', `LKR ${getTotalRateLKR()}`, true, handleClickPaymentBrief)}
                                {resultsRow('Total (USD)', `$ ${getTotalRateUSD()}`, true)}
                            </Stack>
                        </Box>
                    </Card>

                    {/* Action */}
                    <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                        width='100%'
                        spacing={4}
                        pt={summaryData ? 0 : 6}
                        pb={summaryData ? 2 : 0}
                    >
                        {
                            summaryData
                            ?
                            <Stack 
                                direction='row'
                                justifyContent='center'
                                width='100%'
                                px={{xs: 3, md: 5}}
                                spacing={{xs: 1, md: 2}}
                            >
                                <ButtonComponent 
                                    label='Delete'
                                    fullWidth
                                    onClick={deleteTrip}
                                    inLoading={isLoadingTourDeletion}
                                    variant='outlined'
                                />

                                <ButtonComponent 
                                    label='Edit'
                                    fullWidth
                                    onClick={editATrip}
                                    inLoading={false}
                                />
                            </Stack>
                            :
                            <>
                                <ButtonComponent 
                                    label='Back'
                                    fullWidth
                                    onClick={backToTripCalculations}
                                    variant='outlined'
                                />

                                <ButtonComponent 
                                    label={data?.id ? 'Update' : 'Save'}
                                    fullWidth
                                    onClick={requestSave}
                                    inLoading={isLoadingTourSave || isLoadingTourEdit}
                                />
                            </>
                        }
                    </Stack>
                </Stack>
            </Box>

            {/* Save or Edit */}
            <DialogComponent 
                open={showSaveDialog}
                title={data?.id ? 'Update' : 'Save'}
                body={<>
                    <FormProviderComponent methods={formMethods} onSubmit={()=>{}}>
                        <TextFieldComponent 
                            name='tripName'
                            placeholder="Your trip name here"
                            fullWidth
                        />
                    </FormProviderComponent>
                </>}
                handleClose={() => setShowSaveDialog(false)}
                proceedText={data?.id ? 'Update' : 'Save'}
                cancelText="Cancel"
                proceedCallback={confirmSave}
                cancelCallback={() => setShowSaveDialog(false)}
                disableProceed={values.tripName.trim().length === 0}
            />

            {/* Delete */}
            <DialogComponent 
                open={showDeleteDialog}
                title="Delete"
                body={<>
                    <Stack
                        direction='column'
                        justifyContent='center'
                        alignItems='center'
                    >
                        <Typography
                            fontWeight={300}
                            fontSize={14}
                            textAlign='justify'
                        >
                            Are you sure to delete the trip details?
                        </Typography>
                    </Stack>
                </>}
                handleClose={() => setShowDeleteDialog(false)}
                proceedText="No"
                cancelText="Yes"
                proceedCallback={() => setShowDeleteDialog(false)}
                cancelCallback={confirmDeleteTrip}
            />

            <SnackbarComponent 
                message={snackBar.message}
                color={snackBar.color}
                open={snackBar.open}
                handleClose={() => setSnackBar({
                    message: "",
                    open: false
                })}
            />

            {/* Distance Breakdown */}
            <Menu
                id="demo-positioned-menu"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorElDistanceBrief}
                open={openDistanceBrief}
                onClose={handleCloseDistanceBrief}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
            >
                <MenuItem 
                    onClick={handleCloseDistanceBrief}
                    sx={{ '&:hover': { backgroundColor: 'white' } }}
                >
                    <TableContainer>
                        <Table size="small" aria-label="a dense table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Breakdown</TableCell>
                                    <TableCell align="right">Distance&nbsp;(Km)</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell component="th" scope="row">Base Distance</TableCell>
                                    <TableCell align="right">{data?.distance}</TableCell>
                                </TableRow>

                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell component="th" scope="row">Additional Tour Distance</TableCell>
                                    <TableCell align="right">{getAdditionalDayBasedDistance()}</TableCell>
                                </TableRow>

                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell component="th" scope="row">Threshold Adjustment</TableCell>
                                    <TableCell align="right">{getAdditionalKm()}</TableCell>
                                </TableRow>

                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell component="th" scope="row">Rounded</TableCell>
                                    <TableCell align="right">{getRoundedKmOffset()}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </MenuItem>
            </Menu>

            {/* Payment Breakdown */}
            <Menu
                id="demo-positioned-menu"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorElPaymentBrief}
                open={openPaymentBrief}
                onClose={handleClosePaymentBrief}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
            >
                <MenuItem 
                    onClick={handleClosePaymentBrief}
                    sx={{ '&:hover': { backgroundColor: 'white' } }}
                >
                    <TableContainer>
                        <Table size="small" aria-label="a dense table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Breakdown</TableCell>
                                    <TableCell align="right">Payment&nbsp;(LKR)</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell component="th" scope="row">Actual Rate Without Batta</TableCell>
                                    <TableCell align="right">{getActualRateWithoutBatta()}</TableCell>
                                </TableRow>

                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell component="th" scope="row">Total Batta</TableCell>
                                    <TableCell align="right">{getBattaRate()}</TableCell>
                                </TableRow>

                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell component="th" scope="row">
                                        {
                                            getSeasonType(data) === "Off"
                                            ?
                                            'Off Season Rate'
                                            :
                                            `${getSeasonType(data)} Season Rate ($5 x ${data?.days} Days)`
                                        }
                                    </TableCell>
                                    <TableCell align="right">{getSeasonalRate() > 0 ? numberWithCommas(getSeasonalRate().toFixed(2)) : 0}</TableCell>
                                </TableRow>

                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell component="th" scope="row">{`Margin ($${getMarginUSDRate(data)})`}</TableCell>
                                    <TableCell align="right">{getMarginLKRRate() > 0 ? numberWithCommas(getMarginLKRRate().toFixed(2)) : 0}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </MenuItem>
            </Menu>
        </>
    );
}

export default CalculatorResults;